import React, { useState, useContext, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, FormControl, TextField, FormHelperText, Typography, Grid, MenuItem, Collapse, useTheme, useMediaQuery, Checkbox, FormControlLabel } from "@mui/material";
import { styled } from "@mui/system";
import validator from "validator";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { isValidPhoneNumber, parsePhoneNumber } from "libphonenumber-js";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import PhoneInput from "react-phone-number-input/input";

import Question from "../components/Question";
import ContinueButton from "../components/ContinueButton";
import { MinorUserContext } from "../context/UserNameProvider";
import { calculateAge } from "../lib/utils";
import { textFieldBorderlessSx, singleFieldSx, continueButtonSx, textAreaSx, inputLabelSx, textFieldBorder } from "../lib/uiformatting";
import { reset as insuranceReset } from "../redux/insuranceSlice";
import { reset as stripeReset } from "../redux/stripeSlice";
import {
    setFullName as updateFullName,
    setEmail as updateEmail,
    setPhoneNo as updatePhoneNo,
    setDob as updateDob,
    setPayMethod as updatePayMethod,
    setAddress as updateAddress,
    markUserUpdateReady,
    setPruneAbandonedCart,
    setSmsConsent,
} from "../redux/userSlice";
import setPageAndDeferNavigate from "../lib/setPageAndDeferNavigate";
dayjs.extend(utc);

const StackedGroup = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    width: "100%",
    border: `2px solid ${theme.palette.grayTones?.border ?? "#999"}`,
    borderRadius: "15px",
    backgroundColor: "white",
    marginBottom: "1rem",
    "& > :not(:last-child)": {
        borderBottom: `1px solid ${theme.palette.grayTones?.border ?? "#999"}`,
    },
    "&:focus-within": {
        borderColor: theme.palette.piction.highlight,
    },
}));

const PhoneInputBorderless = styled("input")(({ theme }) => ({
    width: "100%",
    minHeight: "clamp(50px, 6vh, 80px)",
    fontSize: "clamp(12px, 2.5vh, 15px)",
    fontFamily: "Inter, sans-serif",
    fontWeight: 600,
    padding: "12px 32px",
    borderRadius: "100px",
    backgroundColor: "transparent",
    border: "2px solid",
    borderColor: theme.palette.grayTones?.border || "#E0E0E0",
    outline: "none",
    "&:hover": {
        borderColor: theme.palette.grayTones?.border || "#E0E0E0",
    },
    "&:focus": {
        borderColor: "#2200BB",
    },
    "&::placeholder": {
        color: "#9e9e9e",
    },
}));

const Demographic = (props) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const { returning = false, portal = false } = location.state || {};
    const theme = useTheme();
    const user = useSelector((state) => state.user);
    const { setIsMinor } = useContext(MinorUserContext);

    const [firstName, setFirstName] = useState(user.firstName);
    const [lastName, setLastName] = useState(user.lastName);
    const [firstNameError, setFirstNameError] = useState("");
    const [lastNameError, setLastNameError] = useState("");

    const [email, setEmail] = useState(user.email);
    const [emailError, setEmailError] = useState("");
    const [emailFocused, setEmailFocused] = useState(false);
    const [phoneFocused, setPhoneFocused] = useState(false);
    const [dobFocused, setDobFocused] = useState(false);

    const [phoneNoValid, setPhoneNoValid] = useState(true);
    const [firstEnter, setFirstEnter] = useState(true);
    const [phoneNo, setPhoneNo] = useState(user.phoneNo);
    const [smsConsent, updateSmsConsent] = useState(user.smsConsent ?? false);

    const normalizeDob = (val) => {
        if (!val) return null;

        if (dayjs.isDayjs(val)) return val.startOf("day");
        if (val instanceof Date && !Number.isNaN(val.getTime())) return dayjs(val).startOf("day");

        const s = String(val).trim();

        if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return dayjs(s);
        const hasTZ = /\bGMT\b|Z|[+-]\d{2}:?\d{2}/i.test(s) || s.includes("T");
        if (hasTZ) {
            const dUtc = dayjs.utc(s);
            return dayjs(dUtc.format("YYYY-MM-DD"));
        }

        const d = new Date(s);
        return Number.isNaN(d.getTime()) ? null : dayjs(d).startOf("day");
    };

    const formatDobYmd = (val) => {
        const n = normalizeDob(val);
        return n && n.isValid() ? n.format("YYYY-MM-DD") : "";
    };

    const [dob, setDob] = useState(normalizeDob(user.dob));
    const [dobError, setDobError] = useState(null);

    const errorMessage = {
        invalidDate: "Please enter a valid date.",
        minDate: "Please enter a valid date.",
        disableFuture: "Please enter a date in the past.",
        required: "This field is required.",
    };

    const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
    const isShortScreen = useMediaQuery("(max-height: 700px)");

    const states = {
        "California": "CA",
        "Connecticut": "CT",
        "Florida": "FL",
        "Massachusetts": "MA",
        "New Hampshire": "NH",
        "Washington": "WA",
        "None of the above": "N/A",
    };

    const allowedStates = ["CT", "FL", "MA", "NH", "WA", "CA", "N/A"];

    const [selectedState, setSelectedState] = useState(
        user.address?.state && allowedStates.includes(user.address.state)
            ? user.address.state
            : user.address?.state == null || user.address.state === ""
                ? ""
                : "N/A"
    );
    const [otherState, setOtherState] = useState(user.address?.state || "");

    const handleStateChange = useCallback((event) => {
        const value = event.target.value;
        setSelectedState(value);
        if (value !== "N/A") {
            setOtherState(value);
            dispatch(updateAddress({ ...user.address, state: value }));
        } else {
            setOtherState("");
        }
    }, [dispatch, user.address]);

    const handleOtherStateChange = useCallback((event) => {
        const value = event.target.value;
        setOtherState(value.toUpperCase());
        dispatch(updateAddress({ ...user.address, state: value.toUpperCase() }));
    }, [dispatch, user.address]);

    const handleDateChange = (newDate) => {
        if (newDate && newDate.isValid()) {
            const maxAge = 120;
            const minAllowedDate = dayjs().subtract(maxAge, 'years');

            if (newDate.isAfter(dayjs())) {
                setDobError('disableFuture');
                setDob(null);
            } else if (newDate.isBefore(minAllowedDate)) {
                setDobError('minDate');
                setDob(null);
            } else {
                setDob(newDate);
                setDobError(null);
            }
        } else {
            setDob(null);
            setDobError('invalidDate');
        }
    };

    const handlePhoneChange = (value) => {
        setPhoneNo(value || '');
        if (value) {
            setPhoneNoValid(isValidPhoneNumber(value, 'US'));
        } else {
            setPhoneNoValid(true);
        }
    };

    const seedFirstName = user.seedFirstName || "";
    const seedLastName = user.seedLastName || "";
    const seedDobYmd = formatDobYmd(user.seedDob);

    const currentDobYmd = formatDobYmd(dob);

    const identityHasChanged =
        (firstName.trim() || "") !== seedFirstName.trim() ||
        (lastName.trim() || "") !== seedLastName.trim() ||
        (currentDobYmd || "") !== (seedDobYmd || "");

    const keepStateValue = selectedState === "N/A" ? (otherState || "") : selectedState;

    const handleSubmit = async () => {
        dispatch(updateFullName({ firstName: firstName.trim(), lastName: lastName.trim() }));
        dispatch(updateEmail(email));
        const dobYmd = dayjs(dob).format("YYYY-MM-DD");
        if (process.env.REACT_APP_OURS_EVENT) {
            // eslint-disable-next-line no-undef
            ours('track', 'Lead', {}, { email: email, phone_number: phoneNo, first_name: firstName, last_name: lastName, date_of_birth: dobYmd });
        }

        if (phoneNoValid && phoneNo) {
            const parsedPhoneNumber = parsePhoneNumber(phoneNo, "US");
            const formattedPhoneNumber = parsedPhoneNumber.format("E.164");
            dispatch(updatePhoneNo(formattedPhoneNumber));
        }

        if (dob) {
            const dobYmd = dayjs(dob).format("YYYY-MM-DD");
            dispatch(updateDob(dobYmd));
            const age = calculateAge(dob.toDate());

            if (age <= 2) {
                setIsMinor(true);
                setPageAndDeferNavigate(dispatch, '/infant', navigate);
            } else if (age < 18) {
                setIsMinor(true);
                setPageAndDeferNavigate(dispatch, '/minor/consent', () =>
                    navigate('/minor/consent', { state: { returning } })
                );
            } else {
                setIsMinor(false);

                if (user.abandonedIntake && identityHasChanged) {
                    dispatch(
                        setPruneAbandonedCart({
                            firstName: firstName.trim(),
                            lastName: lastName.trim(),
                            dob: dobYmd,
                            email,
                            phoneNo:
                                phoneNoValid && phoneNo
                                    ? parsePhoneNumber(phoneNo, "US")?.format?.("E.164")
                                    : "",
                            address: { ...user.address, state: keepStateValue || "" },
                            concern: user.concern || "",
                        })
                    );

                    dispatch(insuranceReset());
                    dispatch(stripeReset());
                }

                if (
                    selectedState === 'N/A' &&
                    otherState &&
                    !allowedStates.includes(otherState.toUpperCase())
                ) {
                    setPageAndDeferNavigate(dispatch, '/other-state', () =>
                        navigate('/other-state', { state: { returning } })
                    );
                } else if (returning) {
                    setPageAndDeferNavigate(dispatch, '/feeling-now', navigate);
                    // TBD - for future self pay only state flows || user.address?.state === "XY" ||
                } else if (user.uberDoc || user.selfPayIntake) {
                    dispatch(updatePayMethod('self'));
                    dispatch(insuranceReset());
                    setPageAndDeferNavigate(dispatch, '/whats-next', navigate);
                } else {
                    setPageAndDeferNavigate(dispatch, '/insurance/provider', navigate);
                }
            }

            dispatch(markUserUpdateReady());
        }
    };

    const handleBack = () => {
        if (returning) {
            setPageAndDeferNavigate(dispatch, '/returning', navigate);
        } else if (portal) {
            setPageAndDeferNavigate(dispatch, '/concern', () =>
                navigate('/concern', { state: { portal: portal } })
            );
        } else {
            setPageAndDeferNavigate(dispatch, '/concern', navigate);
        }
    };

    return (
        <Question
            title="Start your personalized dermatology visit"
            onBack={handleBack}
            input={
                <Box>
                    <FormControl sx={{ width: "100%", mb: "0.5rem" }} required>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <StackedGroup>
                                    <TextField
                                        placeholder="First name"
                                        type="text"
                                        variant="outlined"
                                        color="secondary"
                                        autoComplete="off"
                                        value={firstName}
                                        onChange={(e) => {
                                            if (!e.target.value) {
                                                setFirstNameError("This field is required.");
                                            } else {
                                                setFirstNameError("");
                                            }
                                            setFirstName(e.target.value);
                                        }}
                                        sx={textFieldBorderlessSx}
                                    />
                                    <TextField
                                        placeholder="Last name"
                                        type="text"
                                        variant="outlined"
                                        color="secondary"
                                        autoComplete="off"
                                        value={lastName}
                                        onChange={(e) => {
                                            if (!e.target.value) {
                                                setLastNameError("This field is required.");
                                            } else {
                                                setLastNameError("");
                                            }
                                            setLastName(e.target.value);
                                        }}
                                        sx={textFieldBorderlessSx}
                                    />
                                </StackedGroup>
                                <FormHelperText
                                    error={Boolean(firstNameError) || Boolean(lastNameError)}
                                    sx={{ mt: 0.5 }}
                                >
                                    {(firstNameError && lastNameError)
                                        ? "These fields are required"
                                        : (firstNameError || lastNameError || "")}
                                </FormHelperText>
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    placeholder="example@example.com"
                                    type="email"
                                    variant="outlined"
                                    color="secondary"
                                    autoComplete="email"
                                    value={email}
                                    onFocus={() => setEmailFocused(true)}
                                    onBlur={() => setEmailFocused(false)}
                                    onChange={(e) => {
                                        if (!e.target.value) {
                                            setEmailError("This field is required.");
                                        } else if (!validator.isEmail(e.target.value)) {
                                            setEmailError("Please enter a valid email address.");
                                        } else {
                                            setEmailError("");
                                        }
                                        setEmail(e.target.value);
                                    }}
                                    sx={{
                                        ...textFieldBorder,
                                        "& .MuiOutlinedInput-root": {
                                            ...textFieldBorder["& .MuiOutlinedInput-root"],
                                            "&.Mui-focused fieldset": {
                                                border: "2px solid",
                                                borderColor: "#2200BB",
                                            },
                                        },
                                    }}
                                />
                                <FormHelperText
                                    error={emailError !== ""}
                                    sx={{ mt: 0.5 }}
                                >
                                    {emailFocused && !emailError
                                        ? "We'll send secure updates about your treatment here"
                                        : emailError || ""}
                                </FormHelperText>
                            </Grid>

                            <Grid item xs={12}>
                                <Box>
                                    <PhoneInput
                                        country="US"
                                        placeholder="(234) 567-8901"
                                        value={phoneNo}
                                        onChange={handlePhoneChange}
                                        onBlur={() => {
                                            setFirstEnter(false);
                                            setPhoneFocused(false);
                                        }}
                                        onFocus={() => setPhoneFocused(true)}
                                        inputComponent={PhoneInputBorderless}
                                    />
                                </Box>
                                <FormHelperText
                                    error={!phoneNoValid && !firstEnter}
                                    sx={{ mt: 0.5 }}
                                >
                                    {phoneFocused && phoneNoValid
                                        ? "Quick reminders and prescription alerts"
                                        : (!phoneNoValid && !firstEnter)
                                            ? "Please enter a valid 10-digit US phone number"
                                            : ""}
                                </FormHelperText>
                            </Grid>

                            <Grid item xs={12}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DesktopDatePicker
                                        value={dob}
                                        onChange={handleDateChange}
                                        disableFuture
                                        minDate={dayjs().subtract(120, 'years')}
                                        format="MM/DD/YYYY"
                                        slotProps={{
                                            textField: {
                                                placeholder: "Date of birth - MM/DD/YYYY",
                                                fullWidth: true,
                                                error: !!dobError,
                                                helperText: dobFocused && !dobError
                                                    ? "Helps us recommend the right care"
                                                    : dobError ? errorMessage[dobError] : "",
                                                sx: singleFieldSx,
                                                autoComplete: "bday",
                                                name: "dob",
                                                onFocus: () => setDobFocused(true),
                                                onBlur: () => setDobFocused(false),
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    select
                                    value={selectedState}
                                    onChange={handleStateChange}
                                    variant="outlined"
                                    color="secondary"
                                    autoComplete="off"
                                    fullWidth
                                    SelectProps={{
                                        displayEmpty: true,
                                        renderValue: (value) => {
                                            if (!value) {
                                                return <span style={{ color: "#9e9e9e" }}>Select your state</span>;
                                            }
                                            const fullStateName = Object.entries(states).find(
                                                ([key, code]) => code === value
                                            )?.[0];
                                            return fullStateName || value;
                                        },
                                    }}
                                    sx={singleFieldSx}
                                >
                                    {Object.entries(states).map(([stateName, stateCode]) => (
                                        <MenuItem key={stateCode} value={stateCode}>
                                            {stateName}
                                        </MenuItem>
                                    ))}
                                </TextField>

                                <Collapse in={selectedState === "N/A"} sx={{ width: "100%", mt: 2 }}>
                                    <TextField
                                        placeholder="Enter your current state"
                                        variant="outlined"
                                        color="secondary"
                                        multiline
                                        rows={1}
                                        value={otherState}
                                        onChange={handleOtherStateChange}
                                        fullWidth
                                        sx={{
                                            ...textAreaSx,
                                            "& .MuiInputLabel-root": {
                                                backgroundColor: theme.palette.background.paper,
                                                padding: "0 32px",
                                                transition: "all 0.2s ease-in-out",
                                            },
                                        }}
                                        InputLabelProps={{
                                            sx: {
                                                ...inputLabelSx,
                                                "& .MuiInputLabel-shrink": {
                                                    backgroundColor: theme.palette.background.paper,
                                                },
                                            },
                                        }}
                                    />
                                </Collapse>
                            </Grid>
                        </Grid>
                    </FormControl>
                </Box>
            }
            footer={
                <Box
                    sx={{
                        ...continueButtonSx,
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                    <ContinueButton
                        onClick={handleSubmit}
                        title="Continue"
                        disabled={
                            !firstName ||
                            !lastName ||
                            firstNameError !== "" ||
                            lastNameError !== "" ||
                            emailError !== "" ||
                            !email ||
                            !phoneNoValid ||
                            !phoneNo ||
                            !dob ||
                            !!dobError ||
                            (selectedState === "" || otherState === "") ||
                            !smsConsent
                        }
                        selected={
                            firstName &&
                            lastName &&
                            !firstNameError &&
                            !lastNameError &&
                            email &&
                            !emailError &&
                            phoneNoValid &&
                            phoneNo &&
                            dob &&
                            !dobError
                        }
                    />
                    <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={smsConsent}
                                onChange={(e) => {
                                    updateSmsConsent(e.target.checked);
                                    dispatch(setSmsConsent(e.target.checked));
                                }}
                                color="primary"
                            />
                        }
                        label={
                            <Box sx={{ textAlign: "left" }}>
                                <Typography
                                    sx={{
                                        fontSize: isShortScreen ? "0.65rem" : isDesktop ? "0.8rem" : "0.72rem",
                                        color: "text.secondary",
                                        maxWidth: isDesktop ? "900px" : "none",
                                        margin: 0,
                                    }}
                                >
                                    I agree to receive SMS messages from Piction Health related to appointments and care. Message frequency varies. Reply STOP to opt out or HELP for help. Msg & data rates may apply. See our <a href="https://www.pictionhealth.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and <a href="https://www.pictionhealth.com/terms-of-use" target="_blank" rel="noopener noreferrer">Terms of Service</a>.
                                </Typography>
                            </Box>
                        }
                        sx={{ alignItems: "flex-start", ml: 0, mb: 1 }}
                    />
                    </Box>
                </Box>
            }
            {...props}
        />
    );
};

export default Demographic;
